import { useEffect, useState } from "react";

/**
 * Viewport-relative y of the "reading line": a section becomes active once its
 * top edge scrolls above this line. It has to sit below where a jumped-to
 * section comes to rest — the app header plus the section's own scroll margin,
 * which clears the sticky mobile category rail — or jumping to a section would
 * leave the section above it highlighted.
 */
const READING_LINE_OFFSET_PX = 160;

/** Sub-pixel scroll positions mean the bottom is never exactly reached. */
const SCROLL_END_EPSILON_PX = 2;

const ELEMENT_ID_SEPARATOR = " ";

const SCROLLABLE_OVERFLOW = ["auto", "scroll", "overlay"];

/** The page scrolls inside a layout element, not the window, so the spy has to
 * find that element to know when scrolling has bottomed out. */
function findScrollContainer(element: HTMLElement): HTMLElement {
  let candidate = element.parentElement;
  while (candidate) {
    const { overflowY } = window.getComputedStyle(candidate);
    if (SCROLLABLE_OVERFLOW.includes(overflowY)) {
      return candidate;
    }
    candidate = candidate.parentElement;
  }
  return document.scrollingElement as HTMLElement;
}

function isScrolledToBottom(container: HTMLElement): boolean {
  const remaining =
    container.scrollHeight - container.scrollTop - container.clientHeight;
  return remaining <= SCROLL_END_EPSILON_PX;
}

function findActiveElementId(
  elements: HTMLElement[],
  container: HTMLElement,
): string {
  // The last sections are too short to ever reach the reading line, so at the
  // very bottom the final section is by definition the one being read —
  // without this, jumping to the last section would highlight the one above.
  if (isScrolledToBottom(container)) {
    return elements[elements.length - 1].id;
  }

  // Before any section has crossed the line the first one is active.
  let active = elements[0];
  for (const element of elements) {
    if (element.getBoundingClientRect().top <= READING_LINE_OFFSET_PX) {
      active = element;
    }
  }
  return active.id;
}

/**
 * Scroll-spy over a list of section element ids (in document order), returning
 * the id of the section currently being read. Null only until the first
 * measurement, before which callers should fall back to their first section.
 */
export function useActiveSection(elementIds: string[]): string | null {
  const [activeElementId, setActiveElementId] = useState<string | null>(null);
  // An array prop is a new identity every render, so the listener is keyed on
  // the joined list instead: it only rebuilds when the sections really change.
  const elementIdKey = elementIds.join(ELEMENT_ID_SEPARATOR);

  useEffect(() => {
    if (elementIdKey === "") {
      return;
    }

    const elements = elementIdKey
      .split(ELEMENT_ID_SEPARATOR)
      .map((elementId) => document.getElementById(elementId))
      .filter((element): element is HTMLElement => element !== null);
    if (elements.length === 0) {
      return;
    }

    const container = findScrollContainer(elements[0]);
    let queuedFrame = 0;

    function measure() {
      queuedFrame = 0;
      setActiveElementId(findActiveElementId(elements, container));
    }

    // Scrolling fires far more often than the highlight can change, so the
    // measurement is coalesced to one read per animation frame.
    function handleScroll() {
      if (queuedFrame === 0) {
        queuedFrame = requestAnimationFrame(measure);
      }
    }

    measure();
    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      if (queuedFrame !== 0) {
        cancelAnimationFrame(queuedFrame);
      }
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [elementIdKey]);

  return activeElementId;
}
